import React, {useEffect, useMemo, useState} from 'react';
import Icon from '../icon';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import ProgressiveImage from '../progressiveImage';
import {useSelector} from 'react-redux';
import {RootState, store} from '../../redux/store';
import TextGeneric from '../textGeneric';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {tr} from '../../translations';
import {
  DrawerActions,
  NavigationProp,
  useNavigation,
} from '@react-navigation/native';
import Animated, {
  FadeInLeft,
  FadeOut,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  withDelay,
  FadeInDown,
  FadeOutUp,
} from 'react-native-reanimated';
import Config from 'react-native-config';
import {Platform} from 'react-native';
import '../../utils/webAnimations';
import {toggleSettingsModal} from '../../connected-components/settingsModal/actions';
import Profile from '../../screens/content/profile';
import {LinearGradient} from 'react-native-linear-gradient';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import GenericInput from '../textInput';
import {View} from 'react-native';
import {setSelectedCase} from '../../screens/content/chat/reducer';

const Container = styled(Animated.View)`
  width: 100%;
  border-bottom-left-radius: 40px;
  border-bottom-right-radius: 40px;
  z-index: 99;
`;
const HeaderInputContainer = styled.View<{isMobile?: boolean}>`
  align-items: stretch;
  justify-content: center;
  flex-direction: row;
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  padding: 2px;

  ${({isMobile}) =>
    isMobile
      ? `
    margin-top: 15px;
  `
      : `
    flex: 1;
    margin-top: 2px;
    margin-right: 15px;
  `}
`;
const ContentContainer = styled.View<{topInset: number}>`
  width: 100%;
  padding-top: ${({topInset}) => topInset}px;
  padding-bottom: ${({theme}) => theme.text.s8}px;
  padding-horizontal: ${({theme}) => theme.text.s55}px;
`;
const Row = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;
const MenuIconContainer = styled.TouchableOpacity`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.text.s1 * 1.5}px;
  height: ${({theme}) => theme.text.s1 * 1.5}px;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
`;
const ProfileContainer = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
`;
const ProfileWrapper = styled.View`
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;
const ProfileImageContainer = styled.View`
  border-radius: 100px;
  width: ${({theme}) => theme.text.s1 * 1.3}px;
  height: ${({theme}) => theme.text.s1 * 1.3}px;
  overflow: hidden;
  border-color: ${({theme}) => theme.homeBackground};
  border-width: 2px;
`;
const ProfileImage = styled(ProgressiveImage)`
  width: 100%;
  height: 100%;
`;
const ProfileContentContainer = styled.View`
  flex-direction: column;
`;
const Name = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  padding-horizontal: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.light};
  text-transform: capitalize;
`;
const Spetiality = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  padding-horizontal: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.light};
`;
const PickerContainer = styled.View`
  position: relative;
`;
const PickerButton = styled.TouchableOpacity`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background-color: transparent;
  border-radius: 10px;
  min-width: 120px;
  border-color: ${({theme}) => theme.text.light};
  border-width: 1px;
`;

const PickerButtonText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.light};
  padding-right: 10px;
`;
const DropdownContainer = styled(Animated.View)`
  position: absolute;
  top: 100%;
  min-width: 350px;
  background-color: ${({theme}) => theme.homeBackground + 'EE'};
  border-radius: 15px;
  z-index: 10;
  margin-top: 8px;
  border-width: 1px;
  border-color: rgba(255, 255, 255, 0.3);
  overflow: hidden;
  shadow-color: #000;
  shadow-offset: 0px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 12px;
  elevation: 5;
`;
const DropdownItem = styled.TouchableOpacity`
  padding: 15px;
  border-bottom-width: 1px;
  border-bottom-color: rgba(0, 0, 0, 0.05);
`;
const DropdownItemText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7}px;
`;
const SearchInput = styled.TextInput`
  padding: 15px;
  border-bottom-width: 1px;
  border-bottom-color: rgba(0, 0, 0, 0.05);
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7}px;
  background-color: rgba(0, 0, 0, 0.02);
`;
const CloseButton = styled.TouchableOpacity`
  padding: 15px;
  align-items: center;
  background-color: ${({theme}) => theme.primary}1A;
`;

/**
 * HomeHeader component.
 *
 * @returns {React.ReactElement} The rendered HomeHeader component.
 */
const HomeHeader = (): React.ReactElement => {
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  const {isMobile, wdp} = useResponsiveDimensions();
  const {top: topInset} = useSafeAreaInsets();
  const navigation = useNavigation<NavigationProp<any>>();
  const {currentUser, config} =
    useSelector((state: RootState) => state.auth) || {};
  const {selectedCase} = useSelector((state: RootState) => state.chat);
  const cases = useSelector((state: RootState) => state.chat.cases) || [];
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Memoize user specialty to prevent unnecessary re-renders
  const specialty = useMemo(() => {
    return config?.specialties?.find(s => s.id === currentUser?.specialty_id);
  }, [config?.specialties, currentUser?.specialty_id]);

  const filteredCategories = useMemo(() => {
    return (
      cases
        ?.map(i => ({label: i.label, value: i.value}))
        .filter(category =>
          category.label.toLowerCase().includes(searchQuery.toLowerCase()),
        ) || []
    );
  }, [cases, searchQuery]);

  const scale = useSharedValue(1);
  useEffect(() => {
    scale.value = withDelay(1000, withTiming(1, {duration: 500}));
  }, []);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{scale: scale.value}],
      position: 'absolute',
      pointerEvents: 'none',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      zIndex: -1,
      borderBottomLeftRadius: 40,
      borderBottomRightRadius: 40,
      overflow: 'hidden',
    };
  }, []);

  const renderPickerButton = () => (
    <PickerContainer>
      <PickerButton
        style={{marginTop: 10}}
        onPress={() => setIsPickerVisible(prev => !prev)}>
        <PickerButtonText semiBold numberOfLines={1}>
          EHR Cases
        </PickerButtonText>
        <Icon name="promptSelect" size={theme.text.s4} stroke={theme.primary} />
      </PickerButton>
      {isPickerVisible && (
        <View
          style={{
            flex: 1,
            position: 'absolute',
            top: 50,
            right: 0,
            zIndex: 10,
          }}>
          <DropdownContainer
            style={{
              width: isMobile ? wdp(90) : wdp(40),
              left: isMobile ? 'auto' : 0,
              right: isMobile ? 0 : 'auto',
            }}
            {...(Platform.OS === 'web'
              ? {className: 'screen-fade-in-down'}
              : {entering: FadeInDown.duration(200), exiting: FadeOutUp.duration(150)})}>
            <SearchInput
              placeholder="Search..."
              placeholderTextColor={theme.text.grey}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {filteredCategories.length === 0 ? (
              <DropdownItem>
                <DropdownItemText>No matches found</DropdownItemText>
              </DropdownItem>
            ) : (
              filteredCategories.map((item, index) => (
                <DropdownItem
                  key={index}
                  onPress={() => {
                    store.dispatch(setSelectedCase(item?.value || ''));
                    setIsPickerVisible(false);
                    setSearchQuery('');
                  }}>
                  <DropdownItemText>{item?.label}</DropdownItemText>
                </DropdownItem>
              ))
            )}
            <CloseButton onPress={() => setIsPickerVisible(false)}>
              <TextGeneric bold color={theme.text.error}>
                {tr('app.close')}
              </TextGeneric>
            </CloseButton>
          </DropdownContainer>
        </View>
      )}
    </PickerContainer>
  );

  const renderHeaderInput = () => {
    return (
      <HeaderInputContainer isMobile={isMobile}>
        <GenericInput
          value={selectedCase}
          onChangeText={text => store.dispatch(setSelectedCase(text))}
          placeholder={'Your EHR Case'}
          containerStyle={{flex: 1}}
          inputContainerStyle={{
            backgroundColor: 'transparent',
            borderColor: 'transparent',
          }}
          placeholderTextColor={theme.text.light}
          style={{
            color: theme.text.light,
            paddingTop: 2,
            paddingBottom: 2,
            minHeight: theme.text.s1 * 2.5,
          }}
          multiline
          numberOfLines={4}
        />
      </HeaderInputContainer>
    );
  };

  return (
    <Container>
      <Animated.View pointerEvents={'none'} style={animatedStyle}>
        <LinearGradient
          colors={[
            theme.tabNews.startLinearGradient,
            theme.tabNews.secondLinearGradient,
            theme.tabNews.thirdLinearGradient,
          ]}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 1}}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </Animated.View>
      <ContentContainer topInset={Math.max(topInset, theme.text.s8)}>
        <Row style={{zIndex: 2}}>
          <ProfileWrapper style={{marginRight: 15}}>
            <ProfileContainer
              disabled={Config.DEMO_EMAIL === currentUser?.email}
              style={{
                opacity: Config.DEMO_EMAIL === currentUser?.email ? 0 : 1,
              }}
              activeOpacity={0.8}
              onPress={() =>
                toggleSettingsModal({
                  title: tr('profile.title'),
                  component: () => <Profile />,
                })
              }>
              <ProfileImageContainer>
                <ProfileImage
                  source={{uri: currentUser?.image_url}}
                  defaultImage={require('../../assets/images/user.png')}
                />
              </ProfileImageContainer>
              <ProfileContentContainer>
                <Name bold>
                  {currentUser?.first_name + ' ' + currentUser?.last_name}
                </Name>
                {(currentUser?.email || '').trim().toLowerCase() !==
                  Config.APPLE_EMAIL && (
                  <Animated.View
                    key={specialty?.name || 'init-key'}
                    entering={FadeInLeft}
                    exiting={FadeOut}>
                    <Spetiality semiBold>{specialty?.name || ''}</Spetiality>
                  </Animated.View>
                )}
              </ProfileContentContainer>
            </ProfileContainer>
            {/*{!isMobile && renderPickerButton()}*/}
          </ProfileWrapper>
          {!isMobile && renderHeaderInput()}
          <Animated.View
            style={{
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}>
            <MenuIconContainer
              activeOpacity={0.8}
              onPress={() =>
                navigation?.dispatch(DrawerActions.toggleDrawer())
              }>
              <Icon
                name={'menu'}
                color={theme.secondary}
                size={theme.text.s5}
              />
            </MenuIconContainer>
          </Animated.View>
        </Row>
        {isMobile && renderHeaderInput()}
        {/*{isMobile && renderPickerButton()}*/}
      </ContentContainer>
    </Container>
  );
};

export default React.memo(HomeHeader);
